import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import isArray from 'lodash/isArray';
import i18n from './../../../shared/utils/i18n';
import {
    FOOTBALL_FIRST_HALF_TIME_IN_MINUTES,
    FOOTBALL_SECOND_HALF_TIME_IN_MINUTES,
    FOOTBALL_FIRST_HALF_EXTRA_TIME_MATCH_STATUS,
    FOOTBALL_SECOND_HALF_EXTRA_TIME_MATCH_STATUS,
    FOOTBALL_SECOND_HALF_BREAK_MATCH_STATUS,
    FOOTBALL_PENALTIES_STATUS,
    FIRST_HALF_BREAK_MATCH_STATUS,
    FIRST_HALF_BREAK_TEXT,
    FIRST_HALF_MATCH_STATUS,
    FIRST_HALF_TEXT,
    SECOND_HALF_TEXT,
    SECOND_HALF_MATCH_STATUS,
    GAA_EXTRA_TIME_IN_MINUTES,
    DARTS_LEG_CODE,
    DARTS_SET_CODE,
    CRICKET_NO_MATCH_DEFAULT_SCORE,
    CRICKET_AWAY_TEAM,
    CRICKET_TEST_CRICKET,
    HANDBALL_PENALTIES,
    HANDBALL_FULLTIME,
    HANDBALL_HALFTIME,
    ICE_HOCKEY_PAUSED,
    ICE_HOCKEY_ENDED,
    ICE_HOCKEY_OVERTIME,
    ICE_HOCKEY_PENALTIES,
    ICE_HOCKEY_AFTER_OVERTIME,
    ESPORTS_DECIDER,
 } from './constants';

import { removeParentheses } from '~shared/utils/string';

/**
  * Helps make a column field
  * @param  {*} value The value of the field
  * @param  {Array}  [bemModifiers=[]] The BEM modifiers for the field
  * @return {Object} The column object
  */
 export function makeColumnField(value, bemModifiers = []) {
     return { value, bemModifiers };
 }

/**
 * Returns game score from an event data
 * @param  {Object} eventData The event data
 * @return {Array} The scores in array where the first item is the home score and the second is the
 * away score
 */
export function extractScoreFromEventData(eventData) {
    const score = get(eventData, ['extra_info', 'score'], '').match(/(\d+).*[^\d](\d+)/);
    const homeScoreIndex = 1;
    const awayScoreIndex = 2;
    const defaultScore = 0;
    let homeScore = get(eventData, ['extra_info', 'sb_home_score']);
    let awayScore = get(eventData, ['extra_info', 'sb_away_score']);

    if (isUndefined(homeScore)) {
        homeScore = get(eventData, ['extra_info', 'sb_player1_score']);
    }

    if (isUndefined(homeScore)) {
        homeScore = get(score, homeScoreIndex);
    }

    if (isUndefined(homeScore)) {
        homeScore = defaultScore;
    }

    if (isUndefined(awayScore)) {
        awayScore = get(eventData, ['extra_info', 'sb_player2_score']);
    }

    if (isUndefined(awayScore)) {
        awayScore = get(score, awayScoreIndex);
    }

    if (isUndefined(awayScore)) {
        awayScore = defaultScore;
    }

    return [homeScore, awayScore];
}

/**
 * Returns game time from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object contain the minutes and secods and properties
 */
export function extractTimeFromEventData(eventData) {
    let timeArray;
    let time = get(eventData, ['extra_info', 'time']);

    if (isUndefined(time)) {
        timeArray = get(eventData, ['extra_info', 'sb_time'], '0:0').split(':');
    } else {
        timeArray = time.split(':');
    }

    const minutesIndex = 0;
    const secondsIndex = 1;
    const zero = 0;
    const base = 10;
    const minutes = parseInt(timeArray[minutesIndex], base);
    const seconds = parseInt(timeArray[secondsIndex], base);

    return {
        minutes: (minutes < zero) ? zero : minutes, // Sometimes we get negative time from the backend ¯\_(ツ)_/¯
        seconds,
    };
}

/**
 * Returns tennis set data from an event data
 * @param  {Object} eventData The event data
 * @return {Object} returns the set number
 */
export function extractSetFromEventData(eventData) {
    const defaultSet = 0;
    const base = 10;
    let set = parseInt(eventData.match_status, base);

    if (Number.isNaN(set)) {
        set = defaultSet;
    }

    return {
        set,
    };
}

/**
 * Returns tennis score from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object containing the score data for a tennis match
 */
export function extractTennisScoreFromEventData(eventData) {
    const setScores = get(eventData, ['extra_info', 'setscores'], '0:0')
        .split('-')
        .reverse();

    const gameScore = get(eventData, ['extra_info', 'gamescore'], '0:0')
        .split(':');

    const server = get(eventData, ['extra_info', 'server']);

    return {
        setScores,
        gameScore,
        server,
    };
}

/**
 * Returns badminton score from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object containing the score data for a badminton match
 */
export function extractBadmintonScoreFromEventData(eventData) {
    let gameScores = get(eventData, ['extra_info', 'gamescores']);
    let gameScore = [];
    if (!isUndefined(gameScores)) {
        gameScores = gameScores.split('-').reverse();
        gameScore = gameScores.shift().split(':');
    }

    return {
        gameScores,
        gameScore,
    };
}

/**
 * Returns tennis score from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object containing the score data for a tennis match
 */
export function extractTableTennisScoreFromEventData(eventData) {
    let setScores = get(eventData, ['extra_info', 'setscores']);
    if (!isUndefined(setScores)) {
        setScores = setScores.split('-').reverse();
    }
    let gameScore = get(eventData, ['extra_info', 'score']);
    if (!isUndefined(gameScore)) {
        gameScore = gameScore.split(':');
    }

    return {
        setScores,
        gameScore,
    };
}


/**
 * Checks if it is the first half injury time
 * @param  {String} matchStatus The match status
 * @param  {Number} minutes The minutes
 * @param  {Number} seconds The seconds
 * @return {Boolean} true if it is the first half injury or false if not
 */
export function isFootballFirstHalfInjuryTime(matchStatus, minutes, seconds) {
    const zeroSeconds = 0;

    return isFirstHalf(matchStatus) &&
    (minutes > FOOTBALL_FIRST_HALF_TIME_IN_MINUTES ||
    (minutes === FOOTBALL_FIRST_HALF_TIME_IN_MINUTES && seconds > zeroSeconds));
}

/**
 * Checks if it is the first period of a game
 * @param  {String} matchStatus The match status
 * @return {Boolean} true if it is the first half or false if not
 */
export function isFirstHalf(matchStatus) {
    return matchStatus === FIRST_HALF_MATCH_STATUS;
}

/**
 * Checks if it is the first half break period of a game
 * @param  {String} matchStatus The match status
 * @return {Boolean} true if it is the first half or false if not
 */
export function isFirstHalfBreak(matchStatus) {
    return matchStatus === FIRST_HALF_BREAK_MATCH_STATUS;
}

/**
 * Checks if it is the second period of a game
 * @param  {String} matchStatus The match status
 * @return {Boolean} true if it is the second half or false if not
 */
export function isSecondHalf(matchStatus) {
    return matchStatus === SECOND_HALF_MATCH_STATUS;
}

/**
 * Checks if it is the second half injury time
 * @param  {String} matchStatus The match status
 * @param  {Number} minutes The minutes
 * @param  {Number} seconds The seconds
 * @return {Boolean} true if it is the second half injury or false if not
 */
export function isFootballSecondHalfInjuryTime(matchStatus, minutes, seconds) {
    const zeroSeconds = 0;

    return isSecondHalf(matchStatus) &&
    (minutes > FOOTBALL_SECOND_HALF_TIME_IN_MINUTES ||
    (minutes === FOOTBALL_SECOND_HALF_TIME_IN_MINUTES && seconds > zeroSeconds));
}


/**
 * Checks if it is the second half break period
 * @param  {String} matchStatus The match status
 * @return {Boolean} true if it is the second half break period or false if not
 */
export function isFootballSecondHalfBreak(matchStatus) {
    return matchStatus === FOOTBALL_SECOND_HALF_BREAK_MATCH_STATUS;
}

/**
 * Checks if it is in extra time
 * @param  {String} matchStatus The match status
 * @return {Boolean} true if it is extra time or false if not
 */
export function isFootballExtraTime(matchStatus) {
    return matchStatus === FOOTBALL_FIRST_HALF_EXTRA_TIME_MATCH_STATUS || matchStatus === FOOTBALL_SECOND_HALF_EXTRA_TIME_MATCH_STATUS;
}

/**
 * Checks if it is in penalty shoot out
 * @param  {String} matchStatus The match status
 * @return {Boolean} true or false
 */
export function isFootballPenalties(matchStatus) {
    return matchStatus === FOOTBALL_PENALTIES_STATUS;
}

/**
 * Returns rugby union and rugby league scores and time
 * @param {Object} state The event data
 * @return {Object} {homeScore, awayScore, time}
 */
export function extractRugbyStatusFromEventData(state) {
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    const match_status = get(state, ['match_status'], '');
    let status = '';

    if (isFirstHalf(match_status)) {
        status = i18n.t(FIRST_HALF_TEXT);
    } else if (isSecondHalf(match_status)) {
        status = i18n.t(SECOND_HALF_TEXT);
    } else if (isFirstHalfBreak(match_status)) {
        status = i18n.t(FIRST_HALF_BREAK_TEXT);
    }

    return {
        homeScore,
        awayScore,
        status,
    };
}

/**
* Checks if it is in extra time
* @param  {Number}  minutes the current game time minutes
* @return {Boolean} true if it is extra time
*/
export function isGaaExtraTime(minutes) {
    return minutes > GAA_EXTRA_TIME_IN_MINUTES;
}

/**
 * Returns gaa hurling and gaa football scores and time
 * @param {Object} state The state object to parse
 * @return {Object} {homeScore, awayScore, minutes, extraMinutes}
 */
export function extractGaaStatusFromEventData(state) {
    const [homeScore, awayScore] = extractScoreFromEventData(state);
    let { minutes } = extractTimeFromEventData(state);
    let extraMinutes = false;

    if (isGaaExtraTime(minutes)) {
        extraMinutes = minutes - GAA_EXTRA_TIME_IN_MINUTES;
        minutes = GAA_EXTRA_TIME_IN_MINUTES;
    }

    return {
        homeScore,
        awayScore,
        minutes,
        extraMinutes,
    };
}

/** Gets the quarters from the event data
* @param  {String} matchStatus The match status
* @return {String} The match quarter
*/
export function formatQuarters(matchStatus) {
    return matchStatus.replace(/[^0-9.]/g, '');
}

/**
* Format for period from match status
* @param  {String} matchStatus The match status
* @return {String} The match period
*/
export function formatPeriod(matchStatus) {
    return matchStatus.replace(/[^0-9.]/g, '');
}

/**
* Gets the boxing match round from event data
* @param  {String} event The event data
* @return {String} the round number
*/
export function extractBoxingRoundNumberFromEventData(event) {
    const defaultBoxingRound = 1;

    return get(event, ['extra_info', 'sb_period'], defaultBoxingRound);
}

/**
* Gets the darts status from event data
* @param  {String} event The event data
* @return {Object} The data for the darts status
*/
export function extractDartsStatusDataFromEventData(event) {
    const defaultDartsRound = 1;
    const statusTypes = {'legs': DARTS_LEG_CODE, 'sets_legs': DARTS_SET_CODE};
    const round = get(event, ['extra_info', 'sb_period'], defaultDartsRound);
    const status = statusTypes[get(event, ['match_status'], '').toLowerCase()];
    const scoreText = get(event, ['extra_info', 'score'], '').replace('*', '');

    let scores = {};
    let scoreMatch = null;
    let shouldAppendDefaultLegs = false;

    if (status === DARTS_SET_CODE) {
        scoreMatch = scoreText.match(/(\d)-(\d)\sSets\s(\d+)-(\d+)/);

        if (isNull(scoreMatch)) {
            scoreMatch = scoreText.match(/(\d)-(\d)/);
            shouldAppendDefaultLegs = true;
        }
    }

    if (status === DARTS_LEG_CODE) {
        scoreMatch = scoreText.match(/(\d+)\s-\s(\d+)/);
    }
    const scoreIndexStart = 1;

    if (isArray(scoreMatch)) {
        scores = scoreMatch.slice(scoreIndexStart);
        if (shouldAppendDefaultLegs) {
            scores.push('0', '0');
        }
    }

    return {
        round, status, scores,
    };
}

/**
* Gets the cricket status from event data
* @param  {String} event The event data
* @return {Object} The data for the darts status
*/
export function extractCricketDataFromEventData(event) {
    const inning = get(event, ['extra_info', 'sb_period'], '1');
    const matchScore = get(event, ['extra_info', 'score'], '');
    const matchTime = get(event, ['extra_info', 'matchtime'], '');
    const extraInfoSport = get(event, ['extra_info', 'sport'], '');
    const homeTeamBattingIndex = 0;
    const awayTeamBattingIndex = 1;
    const battingTeamIndexes = { 'home': homeTeamBattingIndex, 'away': awayTeamBattingIndex };
    const battingTeamIndex = battingTeamIndexes[get(event, ['extra_info', 'sb_team'], '')];
    const homeScoreIndex = 1;
    const awayScoreIndex = 2;

    let periodOne = get(event, ['extra_info', 'sb_period_1'], null);
    let scoreText = '';
    let scores = [];

    if (matchTime !== null) {
        scoreText = matchScore.match(/(.*)\s\(.+:\s(.*)\s\(/);

        if (scoreText === null) {
            scoreText = matchScore.match(/(.*)\s\(/);
            scores = [scoreText[homeScoreIndex], CRICKET_NO_MATCH_DEFAULT_SCORE];

            // If the only team that has batted is the away team, then reverse the order
            // of scores so the score will be displayed in the away team place.
            if (battingTeamIndex === awayTeamBattingIndex) {
                scores = [CRICKET_NO_MATCH_DEFAULT_SCORE, scoreText[homeScoreIndex]];
            }
        } else {

            scores = [scoreText[homeScoreIndex], scoreText[awayScoreIndex]];

            if (extraInfoSport !== CRICKET_TEST_CRICKET) {
                // Backend page load returns an object, where web socket messages return a string to decode
                if (typeof periodOne === 'string') {
                    periodOne = JSON.parse(periodOne);
                }

                // If the first team to bat was the away team, the score will have the away team score in first position
                if (periodOne.sb_team.toLowerCase() === CRICKET_AWAY_TEAM) {
                    scores = [scoreText[awayScoreIndex], scoreText[homeScoreIndex]];
                }
            }
        }
    }

    return {
        inning,
        scores,
        battingTeamIndex,
    };
}

/**
* Gets the snooker frame
* @param  {String} event The event data
* @return {String} the frame number
*/
export function extractSnookerFrameNumberFromEventData(event) {
    return get(event, ['extra_info', 'sb_period'], '1');
}

/**
 * Grab the Mma score, round data
 * @param {Object} event The state from which to grab data
 * @returns {Object} round number (if available), general match status (round_x, in_progress, ended)
 */
export function extractMmaDataFromEventData(event) {
    // not much here. we will get a score for the winner on event
    // 'ended' in 'extra_info' but it is not used.
    const matchStatus = get(event, ['match_status'], '');
    // regex - remove all non-digits
    const roundNo = matchStatus.replace(/\D+/, '');

    return {
        roundNo,
        matchStatus,
    };
}

/**
* Gets the Baseball inning and possession
* @param  {String} event The event data
* @return {Object} the inning number and possession
*/
export function extractbaseballInningDataFromEventData(event) {
    const inning = get(event, ['extra_info', 'sb_period'], '1');
    const possess = get(event, ['extra_info', 'sb_possession_team']);

    return {
        inning,
        possess,
    };
}

/**
* Gets the handball period
* @param  {String} event The event data
* @return {Int} the period number
*/
export function extractHandballPeriodFromEventData(event) {
    const period = get(event, ['extra_info', 'sb_period'], '1');

    return period;
}

/**
 * Checks if it is Penalties in Handball event
 * @param  {String} matchStatus The match status
 * @return {Boolean} True for Penalties, False otherwise
 */
export function isHandballPenalties(matchStatus) {

    return matchStatus === HANDBALL_PENALTIES;
}

/**
 * Checks if it is Fulltime in Handball event
 * @param  {String} matchStatus The match status
 * @return {Boolean} True for Fulltime
 */
export function isHandballFullTime(matchStatus) {

    return matchStatus === HANDBALL_FULLTIME;
}

/**
 * Checks if it is Halftime in Handball event
 * @param  {String} matchStatus The match status
 * @return {Boolean} True for Halftime
 */
export function isHandballHalfTime(matchStatus) {

    return matchStatus === HANDBALL_HALFTIME;

}

/**
* Checks if it is an Interval of an Ice-Hockey event
* @param  {String} matchStatus The match status
* @return {Boolean} True for String starting with expected String, False otherwise
*/
export function isIceHockeyInterval(matchStatus) {

   return matchStatus.startsWith(ICE_HOCKEY_PAUSED);

}

/**
* Checks if it is fulltime in Ice-Hockey event
* @param  {String} matchStatus The match status
* @param  {Number} minutes The minutes left in game
* @return {Boolean} True for ENDED, False otherwise
*/
export function isIceHockeyFullTime(matchStatus) {

   return matchStatus === ICE_HOCKEY_ENDED;

}

/**
* Checks if it is Overtime in Ice-Hockey event
* @param  {String} matchStatus The match status
* @return {Boolean} True for OverTime, False otherwise
*/
export function isIceHockeyOverTime(matchStatus) {

   return matchStatus === ICE_HOCKEY_OVERTIME;

}

/**
 * Checks if it is Penalties in Ice-Hockey event
 * @param  {String} matchStatus The match status
 * @return {Boolean} True for Penalties, False otherwise
 */
export function isIceHockeyPenalties(matchStatus) {

    return matchStatus === ICE_HOCKEY_PENALTIES;

}

/**
 * Checks if it is Penalties in Ice-Hockey event
 * @param  {String} matchStatus The match status
 * @return {Boolean} True for Penalties, False otherwise
 */
export function isIceHockeyAfterOverTime(matchStatus) {

    return matchStatus === ICE_HOCKEY_AFTER_OVERTIME;

}

/**
 * Returns Volleyball score from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object containing the score data for a Volleyball match
 */
export function extractVolleyballScoreFromEventData(eventData) {

    let setScores = get(eventData, ['extra_info', 'setscores']);
    const lessOne = 1;
    if (!isUndefined(setScores)) {
        setScores = setScores.split('-');
        const scoresLength = setScores.length;

        if (scoresLength) {
            setScores = setScores[scoresLength - lessOne].split(':');
        }
    }

    let scoreInSets = get(eventData, ['extra_info', 'score_in_sets']);
    if (!isUndefined(scoreInSets)) {
        scoreInSets = scoreInSets.split(':');
    }
    const server = get(eventData, ['extra_info', 'server']);

    return {
        setScores,
        scoreInSets,
        server,
    };
}

/**
* Gets the eSports data
* @param  {String} eventData The event data
* @return {Object} the Game number
*/
export function extractEsportsFromEventData(eventData) {
    let sb_period = get(eventData, ['extra_info', 'sb_period']).toUpperCase();
    let sb_map_number = get(eventData, ['extra_info', 'sb_map_number']);
    let sb_best_of_number = get(eventData, ['extra_info', 'sb_best_of_number']);

    return {
        sb_period,
        sb_map_number,
        sb_best_of_number,
    };
}

/**
* Checks if it is Decider in eSports event
* @param  {String} matchStatus The match status
* @return {Boolean} True for Decider, False otherwise
*/
export function isEsportsDecider(matchStatus) {

    return matchStatus === ESPORTS_DECIDER;

 }

/**
* Gets the Quarter
* @param  {String} event The event data
* @return {String} the Quarter Number
*/
export function extractAmericanFootballDataFromEventData(event) {
    return get(event, ['extra_info', 'sb_period'], '1');

}

/**
 * Returns Australian Rules score data from an event data
 * @param  {Object} eventData The event data
 * @return {Object} An object containing the score data
 */
export function extractAustralianRulesScoreFromEventData(eventData) {
    const quarter = get(eventData, ['extra_info', 'sb_period'], '1');
    const minutes = get(eventData, ['extra_info', 'sb_time'], '0');
    const scoreData = get(eventData, ['extra_info', 'score']); // 3.2(20):1.0(6)
    const scores = scoreData ? scoreData.match(/\((.*?)\)/g) : null; // ['(20)', '(6)']
    const gameScore = scores ? scores.map(removeParentheses) : ['0', '0'];

    return {
        quarter,
        minutes,
        gameScore, // ['20', '6']
    };
}
